import { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Cable,
  Calculator,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Edit,
  ExternalLink,
  Gauge,
  Loader2,
  Network,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { BridgePicker, bridgeName, useClientBridges } from './BridgePicker';
import { BulkBridgeAssign } from './BulkBridgeAssign';
import {
  hexRegister,
  parseRegisterInput,
  VFD_FORMULAS,
  VFD_ROLE_OPTIONS,
  type VfdDrive,
  type VfdItem,
  type VfdItemKind,
  type VfdPresetBrand,
  type VfdPresetModel,
  vfdApi,
} from '../../utils/vfd';

const selectCls = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';
const cellInputCls = 'h-8 text-sm';

const BAUD_RATES = [2400, 4800, 9600, 19200, 38400, 57600, 115200];
const COM_PORTS = Array.from({ length: 20 }, (_, i) => `COM${i + 1}`);

const STATUS_BADGE: Record<string, { label: string; cls: string; icon: any }> = {
  verified: { label: 'Verified from manual', cls: 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10', icon: CheckCircle2 },
  partial: { label: 'Partly verified — check flagged rows', cls: 'border-amber-500/40 text-amber-600 dark:text-amber-400 bg-amber-500/10', icon: AlertTriangle },
  needs_manual: { label: 'Register table needed — fill addresses', cls: 'border-red-500/40 text-red-600 dark:text-red-400 bg-red-500/10', icon: AlertTriangle },
  custom: { label: 'Custom — enter your registers', cls: 'border-slate-500/40 text-muted-foreground bg-muted', icon: Edit },
};

type ItemDraft = VfdItem & { _addrText: string; _maskText: string; _open?: boolean };

type DriveForm = {
  bridgeId: string;
  networkInterface: string;
  name: string;
  location: string;
  description: string;
  brand: string;
  modelId: string;
  modelName: string;
  connectionType: 'rtu' | 'tcp' | 'profinet';
  serialPort: string;
  baudRate: string;
  parity: 'none' | 'even' | 'odd';
  dataBits: string;
  stopBits: string;
  slaveId: string;
  ipAddress: string;
  port: string;
  pollIntervalSec: string;
  timeoutMs: string;
  isActive: boolean;
  ratedKw: string;
  ratedCurrent: string;
  ratedVoltage: string;
  ratedRpm: string;
  maxFrequency: string;
};

const emptyForm: DriveForm = {
  bridgeId: '',
  networkInterface: 'Ethernet',
  name: '',
  location: '',
  description: '',
  brand: '',
  modelId: '',
  modelName: '',
  connectionType: 'rtu',
  serialPort: 'COM1',
  baudRate: '9600',
  parity: 'none',
  dataBits: '8',
  stopBits: '1',
  slaveId: '1',
  ipAddress: '',
  port: '502',
  pollIntervalSec: '1',
  timeoutMs: '1000',
  isActive: true,
  ratedKw: '',
  ratedCurrent: '',
  ratedVoltage: '',
  ratedRpm: '',
  maxFrequency: '50',
};

let draftSeq = 0;
const newId = () => `vi_${Date.now().toString(36)}_${(draftSeq++).toString(36)}`;

// Startdrive-style parameter numbers (r0024, p1082) for PROFINET drives -> 24 / 1082.
const stripParamPrefix = (text: string) => String(text ?? '').trim().replace(/^[rp](?=\d)/i, '');

const toDraft = (it: VfdItem): ItemDraft => ({
  ...it,
  id: it.id || newId(),
  _addrText: it.addr === null || it.addr === undefined ? '' : String(it.addr),
  _maskText: it.mask === undefined ? '' : `0x${it.mask.toString(16).toUpperCase()}`,
});

const fromDraft = ({ _addrText, _maskText, _open, ...it }: ItemDraft): VfdItem => {
  const out: VfdItem = { ...it };
  if (it.kind !== 'derived') {
    const addr = parseRegisterInput(stripParamPrefix(_addrText));
    out.addr = addr === null || Number.isNaN(addr) ? null : addr;
  }
  if (it.kind === 'nonzero') {
    const mask = parseRegisterInput(_maskText);
    out.mask = mask && !Number.isNaN(mask) ? mask : 0xffff;
  }
  return out;
};

const blankItem = (kind: VfdItemKind): ItemDraft => toDraft(
  kind === 'derived'
    ? { id: newId(), name: '', role: '', kind: 'derived', formula: 'pf_from_power', inputs: {}, decimals: 2, unit: '' }
    : { id: newId(), name: '', role: '', kind: 'number', addr: null, func: 3, words: 1, signed: false, scale: 1, unit: '', decimals: 2 },
);

const connectionSummary = (d: VfdDrive) => (d.connectionType === 'profinet'
  ? `PROFINET · ${d.ipAddress} via "${d.networkInterface || 'Ethernet'}"`
  : d.connectionType === 'tcp'
  ? `Modbus TCP · ${d.ipAddress}:${d.port} · unit ${d.slaveId}`
  : `Modbus RTU · ${d.serialPort} · ${d.baudRate} ${d.dataBits}${(d.parity || 'none')[0].toUpperCase()}${d.stopBits} · slave ${d.slaveId}`);

export function VFDDrivesTab() {
  const token = sessionStorage.get()?.token || '';
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const bridges = useClientBridges(token, selectedClient);
  const [drives, setDrives] = useState<VfdDrive[]>([]);
  const [presets, setPresets] = useState<VfdPresetBrand[]>([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<VfdDrive | null>(null);
  const [form, setForm] = useState<DriveForm>(emptyForm);
  const [items, setItems] = useState<ItemDraft[]>([]);
  const [activeStep, setActiveStep] = useState('drive');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<VfdDrive | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [clientList, presetList] = await Promise.all([
          adminApi.getClients(token),
          vfdApi.presets(token).catch(() => [] as VfdPresetBrand[]),
        ]);
        setClients(clientList);
        setPresets(presetList);
        if (clientList.length > 0) setSelectedClient(clientList[0].id);
      } catch (error: any) {
        toast.error(error.message || 'Failed to load clients');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadDrives = async (clientId = selectedClient) => {
    if (!clientId) return;
    setLoading(true);
    try {
      setDrives(await vfdApi.adminList(token, clientId));
    } catch (error: any) {
      toast.error(error.message || 'Failed to load VFD drives');
      setDrives([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (selectedClient) loadDrives(selectedClient); }, [selectedClient]);

  const brandModels = useMemo(() => presets.find((b) => b.brand === form.brand)?.models || [], [presets, form.brand]);
  const selectedModel: VfdPresetModel | undefined = brandModels.find((m) => m.id === form.modelId);
  const set = <K extends keyof DriveForm>(key: K, value: DriveForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const isProfinet = form.connectionType === 'profinet';

  // ---------------------------------------------------------------- dialog
  const openCreate = () => {
    setEditing(null);
    const firstBrand = presets[0];
    const firstModel = firstBrand?.models[0];
    setForm({ ...emptyForm, brand: firstBrand?.brand || '', modelId: firstModel?.id || '', modelName: firstModel?.name || '' });
    setItems((firstModel?.items || []).map((it) => toDraft({ ...it, id: newId() })));
    setActiveStep('drive');
    setDialogOpen(true);
  };

  const openEdit = (d: VfdDrive) => {
    setEditing(d);
    const r = d.ratings || {};
    const s = (v: any) => (v === null || v === undefined ? '' : String(v));
    setForm({
      bridgeId: d.bridgeId || '',
      name: d.name,
      location: d.location || '',
      description: d.description || '',
      brand: d.brand || '',
      modelId: d.modelId || '',
      modelName: d.modelName || '',
      connectionType: d.connectionType,
      networkInterface: d.networkInterface || 'Ethernet',
      serialPort: d.serialPort || 'COM1',
      baudRate: s(d.baudRate || 9600),
      parity: (d.parity || 'none') as DriveForm['parity'],
      dataBits: s(d.dataBits || 8),
      stopBits: s(d.stopBits || 1),
      slaveId: s(d.slaveId || 1),
      ipAddress: d.ipAddress || '',
      port: s(d.port || 502),
      pollIntervalSec: s((d.pollIntervalMs || 1000) / 1000),
      timeoutMs: s(d.timeoutMs || 1000),
      isActive: d.isActive !== false,
      ratedKw: s(r.ratedKw),
      ratedCurrent: s(r.ratedCurrent),
      ratedVoltage: s(r.ratedVoltage),
      ratedRpm: s(r.ratedRpm),
      maxFrequency: s(r.maxFrequency ?? 50),
    });
    setItems((d.items || []).map(toDraft));
    setActiveStep('drive');
    setDialogOpen(true);
  };

  const applyPreset = (brand: string, modelId: string, confirmReplace: boolean) => {
    const model = presets.find((b) => b.brand === brand)?.models.find((m) => m.id === modelId);
    if (model && confirmReplace && items.length > 0
      && !window.confirm(`Load the ${brand} ${model.name} preset? This replaces the current register map.`)) return;
    setForm((f) => ({
      ...f, brand, modelId, modelName: model?.name || '',
      // PROFINET presets (e.g. SINAMICS G120 PN) read parameters, not Modbus registers.
      connectionType: model?.protocol === 'profinet' ? 'profinet' : (f.connectionType === 'profinet' ? 'rtu' : f.connectionType),
    }));
    if (model) setItems(model.items.map((it) => toDraft({ ...it, id: newId() })));
  };

  const updateItem = (id: string, patch: Partial<ItemDraft>) => setItems((list) => list.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const moveItem = (index: number, delta: number) => setItems((list) => {
    const next = [...list];
    const target = index + delta;
    if (target < 0 || target >= next.length) return list;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });

  const validationErrors = useMemo(() => {
    const errs: string[] = [];
    const names = new Set<string>();
    items.forEach((it, i) => {
      const label = it.name.trim() || `Row ${i + 1}`;
      if (!it.name.trim()) errs.push(`Row ${i + 1}: name is required`);
      const key = it.name.trim().toLowerCase();
      if (key && names.has(key)) errs.push(`"${label}": duplicate name`);
      names.add(key);
      if (it.kind !== 'derived') {
        const addr = parseRegisterInput(stripParamPrefix(it._addrText));
        if (addr !== null && (Number.isNaN(addr) || addr < 0 || addr > 65535)) errs.push(`"${label}": invalid address`);
      } else {
        const f = VFD_FORMULAS.find((x) => x.value === it.formula);
        f?.inputs.forEach((inp) => { if (!it.inputs?.[inp.key]) errs.push(`"${label}": choose "${inp.label}" input`); });
      }
    });
    return errs;
  }, [items]);

  const unmappedCount = items.filter((it) => it.kind !== 'derived' && !it._addrText.trim()).length;

  const handleSave = async () => {
    if (!form.name.trim()) { setActiveStep('drive'); toast.error('Drive name is required'); return; }
    if (form.connectionType === 'rtu' && !form.serialPort.trim()) { setActiveStep('connection'); toast.error('COM port is required'); return; }
    if ((form.connectionType === 'tcp' || form.connectionType === 'profinet') && !form.ipAddress.trim()) { setActiveStep('connection'); toast.error('IP address is required'); return; }
    if (form.connectionType === 'profinet' && !form.networkInterface.trim()) { setActiveStep('connection'); toast.error('Network adapter name is required'); return; }
    if (validationErrors.length > 0) { setActiveStep('registers'); toast.error(validationErrors[0]); return; }

    const num = (v: string) => (v.trim() === '' ? null : Number(v));
    const payload: Partial<VfdDrive> & Record<string, any> = {
      bridgeId: form.bridgeId || null,
      name: form.name.trim(),
      location: form.location.trim(),
      description: form.description.trim(),
      brand: form.brand,
      modelId: form.modelId,
      modelName: form.modelName,
      connectionType: form.connectionType,
      serialPort: form.serialPort.trim().toUpperCase(),
      baudRate: Number(form.baudRate),
      parity: form.parity,
      dataBits: Number(form.dataBits),
      stopBits: Number(form.stopBits),
      slaveId: Number(form.slaveId),
      ipAddress: form.ipAddress.trim(),
      networkInterface: form.networkInterface.trim(),
      port: Number(form.port),
      pollIntervalMs: Math.round(Number(form.pollIntervalSec || 1) * 1000),
      timeoutMs: Number(form.timeoutMs || 1000),
      isActive: form.isActive,
      ratings: {
        ratedKw: num(form.ratedKw),
        ratedCurrent: num(form.ratedCurrent),
        ratedVoltage: num(form.ratedVoltage),
        ratedRpm: num(form.ratedRpm),
        maxFrequency: num(form.maxFrequency) ?? 50,
      },
      items: items.map(fromDraft),
    };

    setSaving(true);
    try {
      if (editing) {
        await vfdApi.update(token, selectedClient, editing.id, payload);
        toast.success('VFD updated', { description: 'The bridge picks up changes on its next registration refresh (about 1 minute).' });
      } else {
        await vfdApi.create(token, selectedClient, payload);
        toast.success('VFD added', { description: 'The bridge starts polling it on its next registration refresh (about 1 minute).' });
      }
      setDialogOpen(false);
      loadDrives();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save VFD');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (d: VfdDrive) => {
    try {
      await vfdApi.update(token, selectedClient, d.id, { isActive: !(d.isActive !== false) });
      loadDrives();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await vfdApi.remove(token, selectedClient, deleting.id);
      toast.success(`Deleted ${deleting.name}`);
      setDeleting(null);
      loadDrives();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  // Names other rows can use as inputs for calculated items.
  const inputCandidates = (selfId: string) => items.filter((it) => it.id !== selfId && it.name.trim() && it.kind !== 'bit' && it.kind !== 'nonzero');

  // ---------------------------------------------------------------- render
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold"><Gauge className="h-6 w-6" /> VFD Drives</h2>
          <p className="text-sm text-muted-foreground">
            Variable frequency drives read over Modbus RTU (RS-485) or PROFINET (Ethernet). Clients see them under Live PLC Data → VFD Drives.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select className={`${selectCls} sm:w-[220px]`} value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} aria-label="Client">
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <BulkBridgeAssign token={token} clientId={selectedClient} onDone={() => loadDrives()} />
          <Button onClick={openCreate} disabled={!selectedClient}>
            <Plus className="mr-2 h-4 w-4" /> Add VFD
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>
      ) : drives.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <Gauge className="h-12 w-12 text-muted-foreground opacity-50" />
            <div className="text-lg font-semibold">No VFD drives for this client</div>
            <p className="max-w-md text-sm text-muted-foreground">
              Add a drive, pick its brand and model to load the register map, then set the COM port / IP and slave ID to match the drive’s communication parameters.
            </p>
            <Button onClick={openCreate} disabled={!selectedClient}><Plus className="mr-2 h-4 w-4" /> Add the first VFD</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {drives.map((d) => {
            const registerCount = (d.items || []).filter((it) => it.kind !== 'derived').length;
            const calcCount = (d.items || []).filter((it) => it.kind === 'derived').length;
            const unmapped = (d.items || []).filter((it) => it.kind !== 'derived' && (it.addr === null || it.addr === undefined)).length;
            return (
              <Card key={d.id} className={d.isActive === false ? 'opacity-60' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="truncate text-lg">{d.name}</CardTitle>
                      <CardDescription className="truncate">{[d.brand, d.modelName].filter(Boolean).join(' · ') || 'Custom drive'}{d.location ? ` — ${d.location}` : ''}</CardDescription>
                    </div>
                    <Switch checked={d.isActive !== false} onCheckedChange={() => toggleActive(d)} aria-label="Active" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {d.connectionType === 'rtu' ? <Cable className="h-4 w-4" /> : <Network className="h-4 w-4" />}
                    <span className="truncate">{connectionSummary(d)} · {bridgeName(bridges, d.bridgeId) || 'any bridge PC'}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-md bg-muted px-2 py-1">{registerCount} registers</span>
                    {calcCount > 0 && <span className="rounded-md bg-muted px-2 py-1">{calcCount} calculated</span>}
                    <span className="rounded-md bg-muted px-2 py-1">every {((d.pollIntervalMs || 1000) / 1000).toFixed(1)} s</span>
                    {unmapped > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="h-3 w-3" /> {unmapped} without address
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(d)}><Edit className="mr-1.5 h-4 w-4" /> Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleting(d)} aria-label={`Delete ${d.name}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ---------------- add / edit ---------------- */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="max-h-[92vh] w-[96vw] overflow-y-auto sm:max-w-[1200px] [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add VFD drive'}</DialogTitle>
            <DialogDescription>Only reads are performed — the dashboard never writes to the drive.</DialogDescription>
          </DialogHeader>

          <Tabs value={activeStep} onValueChange={setActiveStep}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="drive">1 · Drive</TabsTrigger>
              <TabsTrigger value="connection">2 · Connection</TabsTrigger>
              <TabsTrigger value="registers">
                3 · Registers ({items.length}){validationErrors.length > 0 && <AlertTriangle className="ml-1.5 h-3.5 w-3.5 text-amber-500" />}
              </TabsTrigger>
            </TabsList>

            {/* Step 1: drive */}
            <TabsContent value="drive" className="space-y-5 pt-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="vfd-name">Drive name *</Label>
                  <Input id="vfd-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Raw Water Pump VFD-1" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="vfd-loc">Location / equipment</Label>
                  <Input id="vfd-loc" value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Pump house, Line 2" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vfd-desc">Description</Label>
                <Textarea id="vfd-desc" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Optional notes shown on the client screen" />
              </div>

              <div className="rounded-lg border p-4">
                <div className="mb-3 text-sm font-semibold">Brand & model</div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Brand</Label>
                    <select
                      className={selectCls}
                      value={form.brand}
                      onChange={(e) => {
                        const brand = e.target.value;
                        const first = presets.find((b) => b.brand === brand)?.models[0];
                        applyPreset(brand, first?.id || '', true);
                      }}
                    >
                      {presets.map((b) => <option key={b.brand} value={b.brand}>{b.brand}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Model</Label>
                    <select className={selectCls} value={form.modelId} onChange={(e) => applyPreset(form.brand, e.target.value, true)}>
                      {brandModels.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                </div>
                {selectedModel && (() => {
                  const badge = STATUS_BADGE[selectedModel.status] || STATUS_BADGE.custom;
                  const Icon = badge.icon;
                  return (
                    <div className="mt-3 space-y-2">
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${badge.cls}`}><Icon className="h-3.5 w-3.5" /> {badge.label}</span>
                      {selectedModel.note && <p className="text-sm text-muted-foreground">{selectedModel.note}</p>}
                      {selectedModel.source && (
                        <a href={selectedModel.source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                          Manual / source <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      <div>
                        <Button type="button" variant="outline" size="sm" onClick={() => applyPreset(form.brand, form.modelId, true)}>
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reload preset register map
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="rounded-lg border p-4">
                <div className="mb-1 text-sm font-semibold">Motor / drive ratings</div>
                <p className="mb-3 text-xs text-muted-foreground">Used for gauge ranges and “% load” bars on the client screen. Take them from the motor nameplate.</p>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                  {([
                    ['maxFrequency', 'Max frequency (Hz)', '50'],
                    ['ratedKw', 'Rated power (kW)', '7.5'],
                    ['ratedCurrent', 'Rated current (A)', '15'],
                    ['ratedVoltage', 'Rated voltage (V)', '415'],
                    ['ratedRpm', 'Rated speed (rpm)', '1450'],
                  ] as const).map(([key, label, ph]) => (
                    <div key={key} className="space-y-1.5">
                      <Label className="text-xs">{label}</Label>
                      <Input type="number" min={0} step="any" value={form[key]} onChange={(e) => set(key, e.target.value)} placeholder={ph} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Step 2: connection */}
            <TabsContent value="connection" className="space-y-5 pt-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {([
                  { id: 'rtu', title: 'Modbus RTU', sub: 'RS-485 via USB/serial adapter on the bridge PC', icon: Cable },
                  { id: 'profinet', title: 'PROFINET (Ethernet)', sub: 'SINAMICS G120 & other PROFIdrive drives — parameters r0024…', icon: Network },
                ] as const).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => set('connectionType', opt.id)}
                    className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors ${form.connectionType === opt.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:border-primary/50'}`}
                    aria-pressed={form.connectionType === opt.id}
                  >
                    <opt.icon className="mt-0.5 h-5 w-5" />
                    <div>
                      <div className="font-semibold">{opt.title}</div>
                      <div className="text-xs text-muted-foreground">{opt.sub}</div>
                    </div>
                  </button>
                ))}
              </div>

              <BridgePicker bridges={bridges} value={form.bridgeId} onChange={(v) => set('bridgeId', v)} id="vfd-bridge-pc" />

              {form.connectionType === 'rtu' ? (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label>COM port *</Label>
                    <Input list="vfd-com-ports" value={form.serialPort} onChange={(e) => set('serialPort', e.target.value)} placeholder="COM3" />
                    <datalist id="vfd-com-ports">{COM_PORTS.map((p) => <option key={p} value={p} />)}</datalist>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Baud rate</Label>
                    <select className={selectCls} value={form.baudRate} onChange={(e) => set('baudRate', e.target.value)}>
                      {BAUD_RATES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Slave ID (1–247)</Label>
                    <Input type="number" min={1} max={247} value={form.slaveId} onChange={(e) => set('slaveId', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Parity</Label>
                    <select className={selectCls} value={form.parity} onChange={(e) => set('parity', e.target.value as DriveForm['parity'])}>
                      <option value="none">None</option>
                      <option value="even">Even</option>
                      <option value="odd">Odd</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Data bits</Label>
                    <select className={selectCls} value={form.dataBits} onChange={(e) => set('dataBits', e.target.value)}>
                      <option value="8">8</option>
                      <option value="7">7</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Stop bits</Label>
                    <select className={selectCls} value={form.stopBits} onChange={(e) => set('stopBits', e.target.value)}>
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </select>
                  </div>
                </div>
              ) : form.connectionType === 'profinet' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label>Drive IP address *</Label>
                      <Input value={form.ipAddress} onChange={(e) => set('ipAddress', e.target.value)} placeholder="192.168.0.1" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Bridge PC network adapter *</Label>
                      <Input list="vfd-pn-ifaces" value={form.networkInterface} onChange={(e) => set('networkInterface', e.target.value)} placeholder="Ethernet" />
                      <datalist id="vfd-pn-ifaces">{['Ethernet', 'Ethernet 2', 'Ethernet 3', 'Local Area Connection'].map((n) => <option key={n} value={n} />)}</datalist>
                      <p className="text-[11px] text-muted-foreground">Name exactly as in Windows → Network Connections.</p>
                    </div>
                  </div>
                  <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 text-sm">
                    <div className="mb-1 font-medium">PROFINET needs on the bridge PC</div>
                    <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground">
                      <li>Python 3 with <code>pip install profinet-py</code>, and <b>Npcap</b> installed.</li>
                      <li>The bridge running with admin rights (the Windows service does).</li>
                      <li>The adapter in the drive's IP range, e.g. drive 192.168.0.1 → PC 192.168.0.50.</li>
                      <li>If Python isn't on PATH, set <code>PROFINET_PYTHON=C:\Path\to\python.exe</code> in the bridge <code>.env</code>.</li>
                    </ul>
                    <p className="mt-1 text-xs text-muted-foreground">Only reads — same parameter channel as Startdrive online. If the link drops, the bridge retries every 1–5 s until the drive answers.</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label>IP address *</Label>
                    <Input value={form.ipAddress} onChange={(e) => set('ipAddress', e.target.value)} placeholder="192.168.1.50" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Port</Label>
                    <Input type="number" value={form.port} onChange={(e) => set('port', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Unit ID</Label>
                    <Input type="number" min={1} max={247} value={form.slaveId} onChange={(e) => set('slaveId', e.target.value)} />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Update every (seconds)</Label>
                  <Input type="number" min={0.5} max={60} step={0.5} value={form.pollIntervalSec} onChange={(e) => set('pollIntervalSec', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Reply timeout (ms)</Label>
                  <Input type="number" min={200} max={10000} step={100} value={form.timeoutMs} onChange={(e) => set('timeoutMs', e.target.value)} />
                </div>
                <div className="flex items-end gap-3 pb-1">
                  <Switch id="vfd-active" checked={form.isActive} onCheckedChange={(v) => set('isActive', v)} />
                  <Label htmlFor="vfd-active">Active (polled by bridge)</Label>
                </div>
              </div>

              {!isProfinet && <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                <div className="mb-1 font-medium text-foreground">Commissioning tips</div>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Baud, parity, stop bits and slave ID must match the drive’s own communication parameters.</li>
                  <li>RS-485: if nothing answers, swap the A/B wires and check the 120 Ω terminator at the far end.</li>
                  <li>A VFD can share a COM port with energy meters — use identical baud / parity / stop bits and a unique slave ID.</li>
                </ul>
              </div>}
            </TabsContent>

            {/* Step 3: registers */}
            <TabsContent value="registers" className="space-y-3 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  {isProfinet ? (
                    <>Enter parameter numbers as shown in Startdrive: <code>r0024</code> or just <code>24</code>. Index is the parameter subindex (usually 0). Status words: use “Status bit” with the bit number.</>
                  ) : (
                    <>Addresses as sent on the wire (0-based). If the manual shows 40001-style numbers, subtract 40001. Hex like <code>0x2103</code> or <code>2103H</code> works.</>
                  )}
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setItems((l) => [...l, { ...blankItem('number'), _open: true }])}><Plus className="mr-1.5 h-3.5 w-3.5" /> Register</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setItems((l) => [...l, { ...blankItem('derived'), _open: true }])}><Calculator className="mr-1.5 h-3.5 w-3.5" /> Calculated</Button>
                </div>
              </div>

              {unmappedCount > 0 && (
                <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> {unmappedCount} register{unmappedCount > 1 ? 's have' : ' has'} no address yet and will show “N/A” until filled.
                </div>
              )}

              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="bg-muted/50 text-xs text-muted-foreground">
                    <tr className="text-left">
                      <th className="w-8 px-2 py-2" />
                      <th className="px-2 py-2 font-medium">Name</th>
                      <th className="px-2 py-2 font-medium">Shows as</th>
                      <th className="px-2 py-2 font-medium">Type</th>
                      <th className="px-2 py-2 font-medium">{isProfinet ? 'Source' : 'Function'}</th>
                      <th className="px-2 py-2 font-medium">{isProfinet ? 'Parameter (r-no.)' : 'Address'}</th>
                      <th className="px-2 py-2 font-medium">{isProfinet ? 'Index' : 'Size'}</th>
                      <th className="px-2 py-2 font-medium">Scale</th>
                      <th className="px-2 py-2 font-medium">Unit</th>
                      <th className="px-2 py-2 font-medium">Dec.</th>
                      <th className="w-24 px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && (
                      <tr><td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">No registers. Add one, or pick a model on the Drive step to load its map.</td></tr>
                    )}
                    {items.map((it, index) => {
                      const isDerived = it.kind === 'derived';
                      const addr = parseRegisterInput(stripParamPrefix(it._addrText));
                      const addrInvalid = addr !== null && (Number.isNaN(addr) || addr < 0 || addr > 65535);
                      const formula = VFD_FORMULAS.find((f) => f.value === it.formula);
                      return [
                        <tr key={it.id} className={`border-t ${it.hidden ? 'opacity-60' : ''}`}>
                          <td className="px-2 py-1.5">
                            <button type="button" className="rounded p-1 hover:bg-muted" onClick={() => updateItem(it.id, { _open: !it._open })} aria-label="More options" aria-expanded={!!it._open}>
                              {it._open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="px-2 py-1.5"><Input className={`${cellInputCls} min-w-[150px]`} value={it.name} onChange={(e) => updateItem(it.id, { name: e.target.value })} placeholder="e.g. DC Bus Voltage" /></td>
                          <td className="px-2 py-1.5">
                            <select className={`${selectCls} h-8 min-w-[130px]`} value={it.role} onChange={(e) => updateItem(it.id, { role: e.target.value as any })}>
                              {VFD_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              className={`${selectCls} h-8 min-w-[120px]`}
                              value={it.kind}
                              onChange={(e) => {
                                const kind = e.target.value as VfdItemKind;
                                const patch: Partial<ItemDraft> = { kind };
                                if (kind === 'derived') Object.assign(patch, { formula: it.formula || 'pf_from_power', inputs: it.inputs || {} });
                                else Object.assign(patch, { func: it.func || 3, words: it.words || 1, scale: it.scale ?? 1 });
                                if (kind === 'bit' || kind === 'nonzero') Object.assign(patch, { trueText: it.trueText || 'ON', falseText: it.falseText || 'OFF' });
                                updateItem(it.id, patch);
                              }}
                            >
                              <option value="number">Number</option>
                              <option value="bit">Status bit</option>
                              <option value="nonzero">Code (≠0 = on)</option>
                              <option value="derived">Calculated</option>
                            </select>
                          </td>
                          {isDerived ? (
                            <td colSpan={4} className="px-2 py-1.5 text-xs text-muted-foreground">
                              <span className="inline-flex items-center gap-1"><Calculator className="h-3.5 w-3.5" /> {formula?.label || 'Choose a formula'}</span>
                            </td>
                          ) : (
                            <>
                              <td className="px-2 py-1.5">
                                {isProfinet ? (
                                  <span className="inline-flex h-8 items-center rounded-md border border-dashed px-2 text-xs text-muted-foreground">PROFIdrive</span>
                                ) : (
                                  <select className={`${selectCls} h-8 min-w-[112px]`} value={it.func || 3} onChange={(e) => updateItem(it.id, { func: Number(e.target.value) as 3 | 4 })}>
                                    <option value={3}>03 Holding</option>
                                    <option value={4}>04 Input</option>
                                  </select>
                                )}
                              </td>
                              <td className="px-2 py-1.5">
                                <Input
                                  className={`${cellInputCls} w-[110px] font-mono ${addrInvalid ? 'border-red-500' : ''}`}
                                  value={it._addrText}
                                  onChange={(e) => updateItem(it.id, { _addrText: e.target.value })}
                                  placeholder="—"
                                  aria-invalid={addrInvalid}
                                />
                                {addr !== null && !addrInvalid && (
                                  <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                                    {isProfinet ? `r${String(addr).padStart(4, '0')}${it.paramIndex ? `[${it.paramIndex}]` : ''}` : `${addr} · ${hexRegister(addr)}`}
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-1.5">
                                {isProfinet ? (
                                  <Input className={`${cellInputCls} w-[72px]`} type="number" min={0} max={255} title="Parameter index (subindex)" value={it.paramIndex ?? 0} onChange={(e) => updateItem(it.id, { paramIndex: Number(e.target.value) || 0 })} />
                                ) : (
                                  <select className={`${selectCls} h-8 min-w-[88px]`} value={it.words || 1} onChange={(e) => updateItem(it.id, { words: Number(e.target.value) as 1 | 2 })}>
                                    <option value={1}>16-bit</option>
                                    <option value={2}>32-bit</option>
                                  </select>
                                )}
                              </td>
                              <td className="px-2 py-1.5">
                                <Input className={`${cellInputCls} w-[90px]`} type="number" step="any" value={it.kind === 'number' ? (it.scale ?? 1) : ''} disabled={it.kind !== 'number'} onChange={(e) => updateItem(it.id, { scale: e.target.value === '' ? 1 : Number(e.target.value) })} />
                              </td>
                            </>
                          )}
                          <td className="px-2 py-1.5"><Input className={`${cellInputCls} w-[70px]`} value={it.unit || ''} onChange={(e) => updateItem(it.id, { unit: e.target.value })} placeholder="unit" disabled={it.kind === 'bit' || it.kind === 'nonzero'} /></td>
                          <td className="px-2 py-1.5"><Input className={`${cellInputCls} w-[56px]`} type="number" min={0} max={6} value={it.decimals ?? 2} onChange={(e) => updateItem(it.id, { decimals: Number(e.target.value) })} disabled={it.kind === 'bit' || it.kind === 'nonzero'} /></td>
                          <td className="px-2 py-1.5">
                            <div className="flex justify-end gap-0.5">
                              <button type="button" className="rounded p-1 hover:bg-muted disabled:opacity-30" disabled={index === 0} onClick={() => moveItem(index, -1)} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
                              <button type="button" className="rounded p-1 hover:bg-muted disabled:opacity-30" disabled={index === items.length - 1} onClick={() => moveItem(index, 1)} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
                              <button type="button" className="rounded p-1 hover:bg-muted" onClick={() => setItems((l) => l.filter((x) => x.id !== it.id))} aria-label="Delete row"><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                            </div>
                          </td>
                        </tr>,
                        it._open && (
                          <tr key={`${it.id}-more`} className="bg-muted/30">
                            <td />
                            <td colSpan={10} className="px-2 py-3">
                              <div className="grid gap-3 md:grid-cols-4">
                                {!isDerived && (
                                  <>
                                    <label className="flex items-center gap-2 text-sm">
                                      <Switch checked={!!it.signed} onCheckedChange={(v) => updateItem(it.id, { signed: v })} /> Signed value
                                    </label>
                                    {it.words === 2 && (
                                      <div className="space-y-1">
                                        <Label className="text-xs">Word order (32-bit)</Label>
                                        <select className={`${selectCls} h-8`} value={it.wordOrder || 'big'} onChange={(e) => updateItem(it.id, { wordOrder: e.target.value as any })}>
                                          <option value="big">High word first (standard)</option>
                                          <option value="little">Low word first (swapped)</option>
                                        </select>
                                      </div>
                                    )}
                                  </>
                                )}
                                {it.kind === 'bit' && (
                                  <div className="space-y-1">
                                    <Label className="text-xs">Bit number (0–15)</Label>
                                    <Input className={cellInputCls} type="number" min={0} max={31} value={it.bit ?? 0} onChange={(e) => updateItem(it.id, { bit: Number(e.target.value) })} />
                                  </div>
                                )}
                                {it.kind === 'nonzero' && (
                                  <>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Mask (e.g. 0xFF)</Label>
                                      <Input className={`${cellInputCls} font-mono`} value={it._maskText} onChange={(e) => updateItem(it.id, { _maskText: e.target.value })} placeholder="0xFFFF" />
                                    </div>
                                    <label className="flex items-center gap-2 text-sm">
                                      <Switch checked={!!it.showCode} onCheckedChange={(v) => updateItem(it.id, { showCode: v })} /> Show code number
                                    </label>
                                  </>
                                )}
                                {isDerived && (
                                  <>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Formula</Label>
                                      <select className={`${selectCls} h-8`} value={it.formula} onChange={(e) => updateItem(it.id, { formula: e.target.value as any, inputs: {} })}>
                                        {VFD_FORMULAS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                      </select>
                                    </div>
                                    {formula?.inputs.map((inp) => (
                                      <div key={inp.key} className="space-y-1">
                                        <Label className="text-xs">{inp.label}</Label>
                                        <select
                                          className={`${selectCls} h-8 ${!it.inputs?.[inp.key] ? 'border-amber-500' : ''}`}
                                          value={it.inputs?.[inp.key] || ''}
                                          onChange={(e) => updateItem(it.id, { inputs: { ...(it.inputs || {}), [inp.key]: e.target.value } })}
                                        >
                                          <option value="">— choose —</option>
                                          {inputCandidates(it.id).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                                        </select>
                                      </div>
                                    ))}
                                    {it.formula === 'above' && (
                                      <div className="space-y-1">
                                        <Label className="text-xs">Threshold</Label>
                                        <Input className={cellInputCls} type="number" step="any" value={it.threshold ?? 0} onChange={(e) => updateItem(it.id, { threshold: Number(e.target.value) })} />
                                      </div>
                                    )}
                                    {it.formula === 'rpm_from_freq' && (
                                      <div className="space-y-1">
                                        <Label className="text-xs">Motor poles</Label>
                                        <Input className={cellInputCls} type="number" min={2} step={2} value={it.poles ?? 4} onChange={(e) => updateItem(it.id, { poles: Number(e.target.value) })} />
                                      </div>
                                    )}
                                  </>
                                )}
                                {(it.kind === 'bit' || it.kind === 'nonzero' || (isDerived && it.formula === 'above')) && (
                                  <>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Text when ON</Label>
                                      <Input className={cellInputCls} value={it.trueText || ''} onChange={(e) => updateItem(it.id, { trueText: e.target.value })} placeholder="RUNNING / FAULT" />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Text when OFF</Label>
                                      <Input className={cellInputCls} value={it.falseText || ''} onChange={(e) => updateItem(it.id, { falseText: e.target.value })} placeholder="STOPPED / NO FAULT" />
                                    </div>
                                    <label className="flex items-center gap-2 text-sm">
                                      <Switch checked={!!it.badWhenTrue} onCheckedChange={(v) => updateItem(it.id, { badWhenTrue: v })} /> ON is an alarm (red)
                                    </label>
                                  </>
                                )}
                                <label className="flex items-center gap-2 text-sm">
                                  <Switch checked={!!it.hidden} onCheckedChange={(v) => updateItem(it.id, { hidden: v })} /> Hide on client screen
                                </label>
                                <div className="space-y-1 md:col-span-4">
                                  <Label className="text-xs">Note (shown as a hint)</Label>
                                  <Input className={cellInputCls} value={it.note || ''} onChange={(e) => updateItem(it.id, { note: e.target.value })} placeholder="e.g. Param 16-13 — scale confirmed against keypad" />
                                </div>
                              </div>
                            </td>
                          </tr>
                        ),
                      ];
                    })}
                  </tbody>
                </table>
              </div>

              {validationErrors.length > 0 && (
                <ul className="space-y-0.5 text-sm text-red-600 dark:text-red-400">
                  {validationErrors.slice(0, 5).map((e) => <li key={e}>• {e}</li>)}
                  {validationErrors.length > 5 && <li>• …and {validationErrors.length - 5} more</li>}
                </ul>
              )}
            </TabsContent>
          </Tabs>

          <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-between">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <div className="flex gap-2">
              {activeStep !== 'registers' && (
                <Button variant="outline" onClick={() => setActiveStep(activeStep === 'drive' ? 'connection' : 'registers')}>Next</Button>
              )}
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editing ? 'Save changes' : 'Add VFD'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------------- delete confirm ---------------- */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>The bridge stops polling this drive and it disappears from the client’s VFD screen. This can’t be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}><Trash2 className="mr-2 h-4 w-4" /> Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
